import { useEffect, useRef, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatPoNumber,
  formatQty,
  CUSTOMER_EMAIL_WAIT_LINE,
  type CustomerEmailWait,
  poReceiveInput,
  parseOpeningCostCents,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { PageRoot } from '../ui/Page';
import { BackCrumb, DetailHeader, MetaStrip, SectionCard } from '../ui/Detail';
import { StatusBadge, Notice, type Tone } from '../ui/Badge';
import { Button, ghostButton } from '../ui/Button';
import { dash } from '@/components/ui/data-table';
import { EmptyState } from '../ui/EmptyState';
import {
  ErrorState,
  UnresolvedState,
  StaleNotice,
  shownState,
  refreshFailed,
} from '../ui/QueryStates';
import { useOnline } from '../ui/useConnection';
import { PoSendSheet } from './PoSendSheet';
import { PoBuilderSheet, ReceiptFeedback, type PoEditInitial } from './PoBuilderSheet';
import { PageSkeleton } from '../ui/Skeleton';
import { Field, field } from '../create/Field';
import { money } from '../money';
import { ProjectLinkCard } from '../projects/ProjectLinkCard';
import { usePoReceiptWrite, useReceiptCompletion } from './usePoReceiptWrite';
import { ReceiptPriceResult } from './PricingHistory';
import { IconInventory } from '../icons';

const PO_STATUS: Record<string, { label: string; tone: Tone }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  sent: { label: 'Ordered', tone: 'info' },
  partially_received: { label: 'Part arrived', tone: 'warn' },
  closed: { label: 'Received', tone: 'success' },
  canceled: { label: 'Canceled', tone: 'neutral' },
};

/**
 * One purchase order (PRD v2 §6.3). The screen is built around the only thing that happens
 * to a PO after it's sent: stuff shows up. Every outstanding line pre-fills with what's
 * still owed, so the common case — the whole order arrived — is one button.
 */
export function PoDetailPage() {
  const { purchaseOrderId } = useParams({
    from: '/office/purchase-orders/$purchaseOrderId',
  });
  const trpc = useTRPC();
  const qc = useQueryClient();
  const q = useQuery({
    ...trpc.purchasing.get.queryOptions({ purchaseOrderId }),
    refetchInterval: (query) =>
      query.state.data?.po.status === 'draft' &&
      query.state.data.delivery?.locked &&
      !query.state.data.delivery.canResolve
        ? 5000
        : false,
  });
  const [sendingSheet, setSendingSheet] = useState<'send' | 'resolve' | null>(null);
  const locations = useQuery(trpc.inventory.locations.queryOptions());
  const catalogue = useQuery({
    ...trpc.inventory.onHand.queryOptions(),
    enabled: q.data?.po.status === 'draft',
  });
  const vendors = useQuery({
    ...trpc.purchasing.vendors.queryOptions(),
    enabled: q.data?.po.status === 'draft',
  });
  const [editing, setEditing] = useState<PoEditInitial | null>(null);
  const [editEpoch, setEditEpoch] = useState(0);
  const online = useOnline();
  const editCatalogueState = shownState(catalogue, catalogue.data);
  const editVendorsState = shownState(vendors, vendors.data);
  const editLocationsState = shownState(locations, locations.data);
  const choicesStale =
    refreshFailed(catalogue, catalogue.data) ||
    refreshFailed(vendors, vendors.data) ||
    refreshFailed(locations, locations.data);
  const choicesReady =
    editCatalogueState === 'ready' &&
    editVendorsState === 'ready' &&
    editLocationsState === 'ready' &&
    !choicesStale;
  const refreshChoices = () => {
    void Promise.allSettled([
      catalogue.refetch(),
      vendors.refetch(),
      locations.refetch(),
    ]);
  };

  const priceCompletion = useReceiptCompletion(purchaseOrderId);
  const [receiving, setReceiving] = useState(false);
  const [qtys, setQtys] = useState<Record<string, string>>({});
  const [costs, setCosts] = useState<Record<string, string>>({});
  const [intoLocationId, setIntoLocationId] = useState('');
  const [warning, setWarning] = useState<string | null>(null);

  const refresh = () => void qc.invalidateQueries();
  const closePo = useMutation(
    trpc.purchasing.close.mutationOptions({ onSuccess: refresh }),
  );
  const cancel = useMutation(
    trpc.purchasing.cancel.mutationOptions({ onSuccess: refresh }),
  );
  const summaryFocus = useRef<HTMLDivElement>(null),
    [restoreFocus, setRestoreFocus] = useState(false);
  const receipt = usePoReceiptWrite((r) => {
    setWarning(
      r.overReceived?.length
        ? `More arrived than was ordered: ${r.overReceived.map((o) => `${o.name} by ${formatQty(o.byHundredths)}`).join(', ')}. It is all on the books.`
        : null,
    );
    setReceiving(false);
    setQtys({});
    setCosts({});
    setRestoreFocus(true);
  }, purchaseOrderId);
  useEffect(() => {
    if (!restoreFocus) return;
    const frame = requestAnimationFrame(() => {
      summaryFocus.current?.focus();
      setRestoreFocus(false);
    });
    return () => cancelAnimationFrame(frame);
  }, [restoreFocus]);
  if (receipt.foreignPending)
    return (
      <PageRoot>
        <ReceiptFeedback write={receipt} />
      </PageRoot>
    );

  if (q.isPending)
    return (
      <>
        <ReceiptFeedback write={receipt} />
        <PageSkeleton />
      </>
    );
  if (q.isError && !q.data) {
    return (
      <PageRoot>
        <BackCrumb to="/inventory" label="Inventory" />
        <ReceiptFeedback write={receipt} />
        <ErrorState
          icon={IconInventory}
          what="this purchase order"
          onRetry={() => void q.refetch()}
        />
      </PageRoot>
    );
  }
  if (!q.data) {
    return (
      <PageRoot>
        <BackCrumb to="/inventory" label="Inventory" />
        <ReceiptFeedback write={receipt} />
        <EmptyState
          icon={IconInventory}
          title="Purchase order not found"
          description="It may have been deleted, or the link is stale."
        />
      </PageRoot>
    );
  }

  const loaded = q.data;
  const { po, vendor, locationName, lines } = loaded;
  const s = PO_STATUS[po.status] ?? { label: po.status, tone: 'neutral' as Tone };
  const outstanding = lines.filter((l) => l.outstandingHundredths > 0);
  const open = po.status !== 'closed' && po.status !== 'canceled';
  const busy =
    sendingSheet !== null ||
    closePo.isPending ||
    cancel.isPending ||
    receipt.locked ||
    editing !== null;
  const error = closePo.error ?? cancel.error;
  const deliveryKnown = Boolean(loaded.delivery),
    deliveryLocked =
      (loaded.delivery?.locked ?? true) &&
      !(po.status !== 'draft' && loaded.delivery?.state === 'accepted');

  const draftFactsReady = (data: NonNullable<typeof q.data>) =>
    Boolean(data.draftRevision) &&
    (data.po.fulfillment === 'deliver' || data.po.fulfillment === 'will_call') &&
    data.lines.every((l) => l.vendorPartNumber !== undefined);
  const snapshot = (data: NonNullable<typeof q.data>): PoEditInitial | null =>
    data.po.status === 'draft' && draftFactsReady(data) && data.po.fulfillment !== null
      ? {
          purchaseOrderId: data.po.id,
          expectedRevision: data.draftRevision!,
          vendorId: data.po.vendorId,
          vendorName: data.vendor.name,
          locationId: data.po.locationId,
          locationName: data.locationName,
          expectedDate: data.po.expectedDate,
          memo: data.po.memo,
          subtotalCents: data.po.subtotalCents,
          fulfillment: data.po.fulfillment,
          lines: data.lines.map((l) => ({
            itemId: l.itemId,
            vendorPartNumber: l.vendorPartNumber,
            name: l.name,
            orderedHundredths: l.orderedHundredths,
            unitCostCents: l.unitCostCents,
          })),
        }
      : null;
  const beginEdit = () => {
    const initial = snapshot(loaded);
    if (!initial) return;
    setEditEpoch((n) => n + 1);
    setEditing(initial);
  };
  const reloadEdit = async () => {
    const fresh = await q.refetch();
    if (fresh.isError || !fresh.data)
      throw new Error('Could not reload the saved draft. Your edits are kept.');
    setEditEpoch((n) => n + 1);
    setEditing(snapshot(fresh.data));
  };

  const startReceiving = () => {
    // Pre-fill everything still owed: "the whole order showed up" is the common case and
    // should cost one tap, not a row of typing.
    setQtys(
      Object.fromEntries(
        outstanding.map((l) => [l.id, formatQty(l.outstandingHundredths)]),
      ),
    );
    setIntoLocationId(po.locationId);
    setWarning(null);
    setReceiving(true);
  };

  const frozen =
    receipt.attempt?.action === 'receive' ? receipt.attempt.input : undefined;
  const formLines = frozen
    ? lines.filter((l) => frozen.lines.some((f) => f.lineId === l.id))
    : outstanding;
  const shownQty = (id: string) =>
    frozen
      ? String(frozen.lines.find((l) => l.lineId === id)?.qty ?? '')
      : (qtys[id] ?? '');
  const shownCost = (id: string) => {
    const f = frozen?.lines.find((l) => l.lineId === id);
    return frozen
      ? f?.unitCostCents === undefined
        ? ''
        : (f.unitCostCents / 100).toFixed(2)
      : (costs[id] ?? '');
  };
  const candidate = poReceiveInput.safeParse({
    purchaseOrderId,
    locationId: intoLocationId || undefined,
    lines: formLines.flatMap((l) => {
      const raw = shownQty(l.id).trim(),
        qty = Number(raw);
      if (raw === '' || qty === 0) return [];
      const rawCost = shownCost(l.id).trim(),
        cost = rawCost === '' ? undefined : (parseOpeningCostCents(rawCost) ?? NaN);
      return [
        { lineId: l.id, qty, ...(cost === undefined ? {} : { unitCostCents: cost }) },
      ];
    }),
  });

  return (
    <PageRoot>
      <BackCrumb to="/inventory" label="Inventory" />
      <div ref={summaryFocus} tabIndex={-1} aria-label="Purchase order summary">
        <DetailHeader
          title={formatPoNumber(po.poNumber)}
          badge={<StatusBadge tone={s.tone}>{s.label}</StatusBadge>}
          right={
            <div className="flex flex-wrap gap-2">
              {po.status === 'draft' ? (
                draftFactsReady(q.data) ? (
                  <Button
                    variant="ghost"
                    disabled={
                      busy ||
                      q.isFetching ||
                      refreshFailed(q, q.data) ||
                      !choicesReady ||
                      !online ||
                      deliveryLocked
                    }
                    onClick={beginEdit}
                  >
                    Edit draft
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    disabled={busy || q.isFetching || !online}
                    onClick={() => void q.refetch()}
                  >
                    Refresh to edit
                  </Button>
                )
              ) : null}
              {po.status === 'draft' || po.status === 'sent' ? (
                <Button
                  variant="ghost"
                  disabled={busy || deliveryLocked || !online}
                  onClick={() => cancel.mutate({ purchaseOrderId, ...idem() })}
                >
                  {po.status === 'sent' ? 'Cancel in ServWell' : 'Cancel'}
                </Button>
              ) : null}
              {po.status === 'draft' ? (
                <Button
                  variant="ghost"
                  disabled={busy || deliveryLocked || !online}
                  onClick={() => setSendingSheet('send')}
                >
                  Send to vendor
                </Button>
              ) : null}
              {open && !receiving ? (
                <Button
                  disabled={busy || deliveryLocked || !online}
                  onClick={startReceiving}
                >
                  Receive
                </Button>
              ) : null}
            </div>
          }
        />
      </div>
      <ReceiptFeedback write={receipt} />
      <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />
      {po.status === 'draft' ? (
        <>
          <StaleNotice show={choicesStale} onRetry={refreshChoices} />
          {editCatalogueState !== 'ready' ? (
            <UnresolvedState
              state={editCatalogueState}
              what="parts for editing this order"
              icon={IconInventory}
              onRetry={() => void catalogue.refetch()}
            />
          ) : null}
          {editVendorsState !== 'ready' ? (
            <UnresolvedState
              state={editVendorsState}
              what="vendors for editing this order"
              icon={IconInventory}
              onRetry={() => void vendors.refetch()}
            />
          ) : null}
          {editLocationsState !== 'ready' ? (
            <UnresolvedState
              state={editLocationsState}
              what="locations for editing this order"
              icon={IconInventory}
              onRetry={() => void locations.refetch()}
            />
          ) : null}
        </>
      ) : null}
      {!deliveryKnown ? (
        <Notice tone="warn">
          Refresh this order before changing it.{' '}
          <Button
            variant="ghost"
            disabled={q.isFetching || !online}
            onClick={() => void q.refetch()}
          >
            Refresh order
          </Button>
        </Notice>
      ) : null}
      {loaded.fulfillmentIssue ? (
        <Notice tone="warn">
          This order's stock destination needs attention. Fix the location before editing
          or preparing a supplier copy. Its fulfillment is not known yet.
        </Notice>
      ) : null}
      {po.status === 'draft' && loaded.delivery?.locked ? (
        <Notice tone="warn">
          {loaded.delivery.state === 'unconfirmed'
            ? 'The vendor email was not confirmed and may have been sent. Check with the supplier before taking another action.'
            : loaded.delivery.state === 'accepted'
              ? 'The mail provider accepted this order. Refresh its status before changing it.'
              : `Vendor email is queued${loaded.delivery.waitingFor ? ` · ${CUSTOMER_EMAIL_WAIT_LINE[loaded.delivery.waitingFor as CustomerEmailWait] ?? 'Waiting to send'}` : ''}. This order stays Draft until the mail provider accepts it.`}
          {loaded.delivery.to ? (
            <p className="mt-1 break-all">To: {loaded.delivery.to}</p>
          ) : null}
          {loaded.delivery.canResolve && loaded.draftRevision ? (
            <Button
              className="mt-2"
              disabled={busy || !online || refreshFailed(q, q.data)}
              onClick={() => setSendingSheet('resolve')}
            >
              I checked with the supplier
            </Button>
          ) : null}
        </Notice>
      ) : null}
      {po.status === 'draft' &&
      loaded.delivery?.state === 'clear' &&
      loaded.delivery.failure ? (
        <Notice tone="warn">
          The vendor email was not sent ({loaded.delivery.failure.replaceAll('-', ' ')}).
          Review the order before trying again or confirming it outside ServWell.
        </Notice>
      ) : null}
      {po.sentDocumentId ? (
        <a
          className={ghostButton}
          href={`/api/documents/purchase-order/${po.sentDocumentId}`}
          target="_blank"
          rel="noreferrer"
        >
          Saved supplier copy
        </a>
      ) : po.source !== 'counter' && po.status !== 'draft' && po.status !== 'canceled' ? (
        <p className="text-sm text-muted-foreground">
          No saved supplier copy for this older order.
        </p>
      ) : null}
      {po.status === 'sent' ? (
        <p className="text-sm text-muted-foreground">
          Cancel in ServWell records the cancellation here. Contact the supplier to cancel
          their order.
        </p>
      ) : null}
      {error ? <Notice tone="danger">{error.message}</Notice> : null}
      {warning ? <Notice tone="warn">{warning}</Notice> : null}
      <ReceiptPriceResult locator={priceCompletion} />
      {po.source === 'counter' ? (
        <Notice tone="info">
          Bought at the counter — recorded as received the moment it was saved.
        </Notice>
      ) : null}

      <MetaStrip
        items={[
          { label: 'Vendor', value: vendor.name },
          { label: 'Goes to', value: locationName },
          { label: 'Expected', value: dash(po.expectedDate) },
          { label: 'Total', value: money(po.subtotalCents) },
        ]}
      />

      {receiving ? (
        <SectionCard title="What showed up">
          <div
            data-receipt-surface
            className="[&_input]:min-h-11 [&_select]:min-h-11 [&_button]:min-h-11"
          >
            <div className="mb-3 max-w-xs">
              <Field label="Into">
                <select
                  className={field}
                  aria-label="Receipt destination"
                  disabled={receipt.locked}
                  value={frozen?.locationId ?? intoLocationId}
                  onChange={(e) => setIntoLocationId(e.target.value)}
                >
                  {(locations.data ?? []).map((l) => (
                    <option key={l.id} value={l.id}>
                      {'   '.repeat(l.depth)}
                      {l.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <ul className="divide-y divide-border">
              {formLines.map((l) => (
                <li key={l.id} className="flex flex-wrap items-end gap-3 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="break-words text-sm font-medium">
                      Line {lines.findIndex((x) => x.id === l.id) + 1}: {l.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatQty(l.outstandingHundredths)} still owed of{' '}
                      {formatQty(l.orderedHundredths)} · current line cost{' '}
                      {money(l.unitCostCents)}
                    </p>
                  </div>
                  <label className="text-xs">
                    <span className="mb-1 block text-muted-foreground">Arrived</span>
                    <input
                      className={`${field} w-24`}
                      disabled={receipt.locked}
                      value={shownQty(l.id)}
                      onChange={(e) => setQtys((p) => ({ ...p, [l.id]: e.target.value }))}
                      inputMode="decimal"
                      aria-label={`Quantity received for line ${lines.findIndex((x) => x.id === l.id) + 1}: ${l.name}`}
                    />
                  </label>
                  <label className="text-xs">
                    <span className="mb-1 block text-muted-foreground">
                      Actual unit cost (optional)
                    </span>
                    <input
                      className={`${field} w-32`}
                      disabled={receipt.locked}
                      value={shownCost(l.id)}
                      onChange={(e) =>
                        setCosts((p) => ({ ...p, [l.id]: e.target.value }))
                      }
                      inputMode="decimal"
                      placeholder={(l.unitCostCents / 100).toFixed(2)}
                      aria-label={`Actual unit cost for line ${lines.findIndex((x) => x.id === l.id) + 1}: ${l.name}`}
                    />
                  </label>
                  <Button
                    variant="ghost"
                    className="min-h-11"
                    disabled={receipt.locked || !online}
                    aria-label={`Confirmed same price for line ${lines.findIndex((x) => x.id === l.id) + 1}: ${l.name}`}
                    onClick={() =>
                      setCosts((p) => ({
                        ...p,
                        [l.id]: (l.unitCostCents / 100).toFixed(2),
                      }))
                    }
                  >
                    Confirmed same price
                  </Button>
                </li>
              ))}
            </ul>

            <p className="mt-3 text-xs text-muted-foreground">
              Blank uses the current line cost for accounting and does not declare the
              supplier's price. Confirm the same price, or enter the actual price,
              including zero. More can arrive than was ordered.
            </p>

            {!candidate.success ? (
              <p role="alert" className="mt-2 text-sm">
                Enter a valid quantity and unit cost for every selected line. Leave
                quantity empty or zero to exclude that line.
              </p>
            ) : null}
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                disabled={busy || deliveryLocked || !online || !candidate.success}
                onClick={() => {
                  if (candidate.success)
                    receipt.begin({ action: 'receive', input: candidate.data });
                }}
              >
                {receipt.attempt?.phase === 'writing' ? 'Receiving…' : 'Book it in'}
              </Button>
              <Button
                variant="ghost"
                disabled={receipt.locked}
                onClick={() => setReceiving(false)}
              >
                Cancel receiving
              </Button>
            </div>
          </div>
        </SectionCard>
      ) : null}

      <SectionCard
        title={`Lines (${lines.length})`}
        right={
          open && outstanding.length > 0 && !receiving ? (
            <Button
              variant="ghost"
              disabled={busy || deliveryLocked || !online}
              onClick={() => closePo.mutate({ purchaseOrderId, ...idem() })}
            >
              Close short
            </Button>
          ) : null
        }
      >
        <ul className="divide-y divide-border">
          {lines.map((l) => {
            const complete = l.outstandingHundredths === 0;
            return (
              <li key={l.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{l.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {dash(l.sku)} · Current line cost: {money(l.unitCostCents)} each
                    <span className="block">
                      Original ordered:{' '}
                      {l.orderedCostProvenance === 'counter_representation'
                        ? 'No prior order — counter purchase'
                        : l.orderedUnitCostCents === null
                          ? 'Unknown — legacy order'
                          : money(l.orderedUnitCostCents)}
                    </span>
                  </p>
                </div>
                <span className="tabular-nums">
                  {formatQty(l.receivedHundredths)} of {formatQty(l.orderedHundredths)}
                </span>
                <StatusBadge tone={complete ? 'success' : 'warn'}>
                  {complete ? 'In' : `${formatQty(l.outstandingHundredths)} to come`}
                </StatusBadge>
                <span className="w-24 text-right tabular-nums">
                  {money(l.lineTotalCents)}
                </span>
              </li>
            );
          })}
        </ul>
      </SectionCard>

      <ReceiptEvidence purchaseOrderId={purchaseOrderId} lines={lines} />
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Buying against a build is how the project's committed money gets counted. */}
        <ProjectLinkCard
          entityType="purchase_order"
          entityId={purchaseOrderId}
          projectId={po.projectId}
          disabled={
            busy || !online || deliveryLocked || q.isFetching || refreshFailed(q, q.data)
          }
        />
        {po.memo ? (
          <SectionCard title="Note">
            <p className="whitespace-pre-wrap text-sm">{po.memo}</p>
          </SectionCard>
        ) : null}
      </div>
      {sendingSheet ? (
        <PoSendSheet
          key={`${purchaseOrderId}:${sendingSheet}`}
          purchaseOrderId={purchaseOrderId}
          poNumber={po.poNumber}
          resolution={
            sendingSheet === 'resolve' && loaded.delivery && loaded.draftRevision
              ? { delivery: loaded.delivery, expectedRevision: loaded.draftRevision }
              : undefined
          }
          onClose={() => setSendingSheet(null)}
        />
      ) : null}
      {editing ? (
        <PoBuilderSheet
          key={editEpoch}
          mode="edit"
          initial={editing}
          items={catalogue.data?.items ?? []}
          locations={locations.data ?? []}
          vendors={vendors.data ?? []}
          onClose={() => setEditing(null)}
          onCreated={() => {
            setEditing(null);
            refresh();
          }}
          onReload={reloadEdit}
        />
      ) : null}
    </PageRoot>
  );
}

function ReceiptEvidence({
  purchaseOrderId,
  lines,
}: {
  purchaseOrderId: string;
  lines: { id: string; name: string }[];
}) {
  const trpc = useTRPC(),
    [offset, setOffset] = useState(0);
  const q = useQuery({
    ...trpc.pricing.receiptEvidence.queryOptions({ purchaseOrderId, offset }),
    retry: false,
  });
  return (
    <SectionCard title="Receipt price history">
      {q.isError ? (
        <div>
          <p>Could not load receipt price history.</p>
          <Button onClick={() => void q.refetch()}>Retry receipt history</Button>
        </div>
      ) : q.isPending ? (
        <p>Loading recorded receipt prices…</p>
      ) : q.data.total === 0 ? (
        <p>
          No recorded price observations. Older receipts have no provable price
          comparison.
        </p>
      ) : (
        <>
          <ol className="space-y-3 text-sm">
            {q.data.rows.map((o) => (
              <li key={o.id} className="break-words border-b border-border pb-3">
                <p className="font-medium">
                  Line {lines.findIndex((l) => l.id === o.lineId) + 1}:{' '}
                  {lines.find((l) => l.id === o.lineId)?.name ?? 'Former order line'}
                </p>
                <p>
                  {new Date(o.receivedAt).toLocaleString()} · {formatQty(o.qtyHundredths)}{' '}
                  received · {money(o.recordedUnitCostCents)} each
                </p>
                <p>
                  {o.classification === 'above_tolerance'
                    ? 'Above recorded tolerance'
                    : o.classification === 'within_tolerance'
                      ? 'Within recorded tolerance'
                      : o.exclusionReason === 'counter'
                        ? 'No prior order — counter purchase'
                        : o.exclusionReason === 'unknown_ordered'
                          ? 'Unknown original price — legacy order'
                          : 'Accounting cost inherited — supplier price not declared'}
                </p>
                <p>Recorded tolerance: {(o.toleranceBps / 100).toFixed(2)}%</p>
              </li>
            ))}
          </ol>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              disabled={offset === 0 || q.isFetching}
              onClick={() => setOffset((v) => v - 25)}
            >
              Previous receipts
            </Button>
            <span>
              {offset + 1}–{offset + q.data.rows.length} of {q.data.total}
            </span>
            <Button
              disabled={offset + 25 >= q.data.total || q.isFetching}
              onClick={() => setOffset((v) => v + 25)}
            >
              Next receipts
            </Button>
          </div>
        </>
      )}
    </SectionCard>
  );
}
